import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react';
import { getAccessToken, getStoredUserRaw } from '@/lib/authStorage';
import type { Project } from '@/lib/api';
import { MAX_MESSAGE_LENGTH } from '@/utils/messageUtils';
import { directMessageDraftKey } from './messengerStorage';

export type DirectMessageDraft = { version: 1; text: string; projectId: number | null };
export type DraftProject = { id: number; name: string };
type DraftEntry = DirectMessageDraft & { key: string; revision: number };
type DraftStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function parseContextId(value: string | null): number | null {
  if (!value || !/^[1-9]\d*$/.test(value)) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id <= 2147483647 ? id : null;
}

export function readContextProjectId(query: Pick<URLSearchParams, 'getAll'>): number | null {
  const values = query.getAll('context_project');
  return values.length === 1 ? parseContextId(values[0]) : null;
}

export function readDirectMessageDraft(storage: DraftStorage, key: string): DirectMessageDraft {
  const empty: DirectMessageDraft = { version: 1, text: '', projectId: null };
  const raw = storage.getItem(key);
  if (!raw) return empty;
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object') return empty;
  const draft = value as Partial<DirectMessageDraft>;
  if (draft.version !== 1 || typeof draft.text !== 'string') return empty;
  return { version: 1, text: draft.text, projectId: parseContextId(String(draft.projectId ?? '')) };
}

export function restoreLegacyDraft(current: DirectMessageDraft, legacy: string | null): DirectMessageDraft {
  return current.text.length || !legacy ? current : { ...current, text: legacy };
}

export function serializeContextMessage(text: string, project: DraftProject | null, origin: string): string {
  if (!project) return text;
  const name = project.name.replace(/[\r\n\x00-\x1f]/g, ' ').trim();
  const result = `${text.trim()}\n\nО проекте «${name}»: ${origin}/projects/${project.id}`;
  if (result.length > MAX_MESSAGE_LENGTH) throw new Error(`Текст вместе со ссылкой на проект превышает ${MAX_MESSAGE_LENGTH} символов. Сократите сообщение или уберите проект.`);
  if (result.split('\n').length > 50) throw new Error('Сократите текст до 48 строк или уберите проект: иначе ссылка будет обрезана при отправке.');
  return result;
}

export async function findDraftProject(
  peerId: number,
  projectId: number,
  loadProject: (peerId: number, limit: number, offset: number, projectId: number) => Promise<Project[]>,
  isCurrent: () => boolean,
): Promise<DraftProject | null> {
  if (!isCurrent()) return null;
  const projects = await loadProject(peerId, 1, 0, projectId);
  if (!isCurrent()) return null;
  const project = projects.find((item) => item.id === projectId && Number(item.owner) === peerId && item.is_public);
  return project ? { id: project.id, name: project.name } : null;
}

function currentViewerId(): number {
  if (typeof window === 'undefined' || !getAccessToken()) return 0;
  try {
    const raw = getStoredUserRaw();
    return parseContextId(String(raw ? JSON.parse(raw)?.id ?? '' : '')) || 0;
  } catch { return 0; }
}

export function useDirectMessageDraft(peerId: number) {
  const [viewerId, setViewerId] = useState(0);
  const [entry, setEntry] = useState<DraftEntry>({ key: '', revision: 0, version: 1, text: '', projectId: null });
  const entryRef = useRef(entry);
  const mountedRef = useRef(false);
  const [legacyAvailable, setLegacyAvailable] = useState(false);
  const [storageNotice, setStorageNotice] = useState('');
  const key = viewerId && peerId ? directMessageDraftKey(viewerId, peerId) : '';
  const activeKeyRef = useRef(key);
  activeKeyRef.current = key;

  useEffect(() => {
    mountedRef.current = true;
    const refresh = () => setViewerId(currentViewerId());
    refresh();
    window.addEventListener('lisad_auth_changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('lisad_auth_changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  useEffect(() => {
    let next: DraftEntry = { key, revision: entryRef.current.revision + 1, version: 1, text: '', projectId: null };
    setLegacyAvailable(false);
    setStorageNotice('');
    if (key) {
      try {
        next = { ...next, ...readDirectMessageDraft(localStorage, key) };
        setLegacyAvailable(Boolean(localStorage.getItem(`lisad_draft_dm_${peerId}`)?.trim()));
      } catch {
        setStorageNotice('Не удалось прочитать черновик на устройстве. Исходные данные не изменены.');
      }
    }
    entryRef.current = next;
    setEntry(next);
  }, [key, peerId]);

  const isCurrent = useCallback(() => Boolean(mountedRef.current && key && activeKeyRef.current === key && entryRef.current.key === key && entryRef.current.revision === entry.revision && currentViewerId() === viewerId), [key, viewerId, entry.revision]);
  const update = useCallback((change: (draft: DraftEntry) => DraftEntry) => {
    if (!isCurrent()) return;
    const next = change(entryRef.current);
    entryRef.current = next;
    setEntry(next);
    try {
      const { version, text, projectId } = next;
      localStorage.setItem(key, JSON.stringify({ version, text, projectId }));
    } catch {
      setStorageNotice('Черновик остаётся в этой вкладке: сохранить его на устройстве не удалось.');
    }
  }, [key, isCurrent]);

  const setText = useCallback((value: SetStateAction<string>) => {
    update((previous) => ({ ...previous, text: typeof value === 'function' ? value(previous.text) : value }));
  }, [update]);
  const setProjectId = useCallback((projectId: number | null) => update((previous) => ({ ...previous, projectId })), [update]);
  const restoreLegacy = useCallback(() => {
    if (!key || activeKeyRef.current !== key || currentViewerId() !== viewerId) return;
    try {
      const legacy = localStorage.getItem(`lisad_draft_dm_${peerId}`);
      update((previous) => ({ ...previous, ...restoreLegacyDraft(previous, legacy) }));
      setLegacyAvailable(false);
    } catch { setStorageNotice('Старый черновик не удалось прочитать. Он не изменён.'); }
  }, [key, peerId, viewerId, update]);

  const ready = Boolean(key && entry.key === key);
  return {
    viewerId, key, ready, isCurrent, revision: entry.revision, text: ready ? entry.text : '', projectId: ready ? entry.projectId : null,
    setText, setProjectId, restoreLegacy, legacyAvailable: ready && legacyAvailable, storageNotice,
  };
}
